import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckBadgeIcon, CheckIcon, ListBulletIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { hasAnyRole } from "../../utils/auth";
import { ROLES } from "../../auth/roles";
import { Button, ConfirmModal, EmptyState, InfoBox, Loading, RowAction, Select, Table, Tag } from "../ui";
import { abroadReviewApi } from "../../api/abroadLoans";
import { errorMessageOf, problemOf } from "../../api/scholarships";
import { formatDateTime, formatScore, outcomeOf, outcomesFor, topOf } from "../scholarships/review/model";
import { ApplicantName } from "../scholarships/review/EligibilityFlag";
import ApprovedFundingDialog from "../scholarships/review/ApprovedFundingDialog";
import { amountsOf, loanSummary } from "../scholarships/funding";
import { REVIEW_KEY, applicationStateOf, awardListKey, fundingEntryOf, useAwardList } from "./review";

const OUTCOMES = outcomesFor(false);

// What is wrong with one entry, from `entries-incomplete`
const ENTRY_PROBLEMS = {
  "not-a-candidate": "is not an application of this call",
  "duplicate-application": "appears twice",
  "rank-required": "needs a reserve rank",
  "duplicate-rank": "has the same reserve rank as another student",
  "rank-not-allowed": "cannot have a reserve rank with this outcome",
  missing: "is missing from the list; reload it",
  "outcome-already-decided": "was decided earlier and cannot change",
};


// A call has closed once published and past its closing date
const closedOf = (call) => call.state === "PUBLISHED" && !call.open && new Date(call.closesAt) <= new Date();

// Reserves are numbered by score; outcomes decided earlier keep theirs and are sent back unchanged
const withReserveRanks = (entries) => {
  let next = 0;
  return [...entries]
    .sort((a, b) => (b.averageScore ?? -1) - (a.averageScore ?? -1))
    .map((e) => (e.decidedEarlier ? e : { ...e, reserveRank: e.outcome === "RESERVE" ? (next += 1) : null }));
};

// The API takes amounts with a currency and a tuition fee; without them an award saves unfunded, and sending it says so
const complete = (amounts) => Boolean(amounts?.currency) && amounts.tuitionFee != null;

/**
 * Who gets an abroad loan. Made from the ranking once the call closes (the best `awardQuota` fully
 * marked applications awarded what they asked for) and adjusted by the HEC Admin or the DG. Sending
 * it publishes the reserve and not-awarded outcomes; awards go to the DG, or to the Minister when the
 * DG sends it. A member of the call's committee cannot send it.
 */
export default function AwardListPanel({ call }) {
  const queryClient = useQueryClient();
  const isDg = hasAnyRole(ROLES.DG);
  const canDecide = hasAnyRole(ROLES.ADMIN) || isDg;
  const sendTo = isDg ? "the Minister" : "the DG";
  const [entries, setEntries] = useState(null); // edited outcomes, before saving
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [problems, setProblems] = useState([]); // one sentence per refused entry
  const [fundingFor, setFundingFor] = useState(null);

  const { data: list, isPending, isError, error, refetch } = useAwardList(call.id);
  const missing = isError && problemOf(error)?.status === 404;

  useEffect(() => {
    setEntries(null);
    setSelected([]);
  }, [list?.version]);

  const refresh = (result) => {
    queryClient.setQueryData(awardListKey(call.id), result);
    queryClient.invalidateQueries({ queryKey: REVIEW_KEY, predicate: (q) => q.queryKey[2] !== "award-list" });
  };

  const shown = entries ?? list?.entries ?? [];
  const nameOf = (id) => shown.find((e) => e.applicationId === id)?.applicantName ?? "An application";

  // `entries[i]…` points at the list as sent, `applications[<id>]` and `entries[<id>]` at an application
  const describe = (problem) =>
    (problem?.errors ?? []).map((e) => {
      const [, inside] = /^\w+\[(.+?)\]/.exec(e.field ?? "") ?? [];
      const name = /^\d+$/.test(inside ?? "") ? shown[Number(inside)]?.applicantName ?? "An application" : nameOf(inside);
      if (e.code === "not-reviewed") return `${name} is still being marked.`;
      if (e.code === "approved-amounts-required") return `${name} needs the approved loan.`;
      return `${name} ${ENTRY_PROBLEMS[e.code] ?? "is not valid"}.`;
    });

  const run = async (action, label) => {
    setBusy(true);
    setProblems([]);
    try {
      refresh((await action()).data);
      return true;
    } catch (err) {
      const problem = problemOf(err);
      console.error(`Failed to ${label} the award list of abroad loan call ${call.id}:`, err);
      if (["entries-incomplete", "applications-not-reviewed", "approved-amounts-required"].includes(problem?.code)) setProblems(describe(problem));
      toast.error(errorMessageOf(err, `Failed to ${label} the award list. Please try again.`));
      if (["changed-meanwhile", "award-list-finalised", "award-list-exists"].includes(problem?.code)) refetch();
      return false;
    } finally {
      setBusy(false);
    }
  };

  const create = async () => {
    if (await run(() => abroadReviewApi.createAwardList(call.id), "create")) toast.success("Award list created from the ranking");
  };

  const save = async () => {
    const body = shown.map(({ applicationId, outcome, reserveRank, reasonCode, approvedAmounts }) => ({
      applicationId,
      outcome,
      reserveRank,
      reasonCode,
      approvedAmounts: outcome === "AWARDED" && complete(approvedAmounts) ? approvedAmounts : null,
    }));
    if (await run(() => abroadReviewApi.saveEntries(call.id, list.version, body), "save")) toast.success("Award list saved");
  };

  const finalise = async () => {
    setConfirming(false);
    if (await run(() => abroadReviewApi.finalise(call.id, list.version), "send")) toast.success(`Award list sent to ${sendTo}`);
  };

  // An award starts from the amounts the student asked for; other outcomes have none
  const setOutcomes = (ids, outcome) =>
    setEntries(
      withReserveRanks(
        shown.map((e) => (ids.includes(e.applicationId) ? { ...e, outcome, approvedAmounts: outcome === "AWARDED" ? e.approvedAmounts ?? amountsOf(e.requested) : null } : e))
      )
    );
  const setAmounts = (applicationId, approvedAmounts) => {
    setEntries(shown.map((e) => (e.applicationId === applicationId ? { ...e, approvedAmounts } : e)));
    setFundingFor(null);
  };
  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  if (isPending) return <Loading label="Loading the award list…" />;

  if (missing) {
    const closed = closedOf(call);
    const top = topOf(call.awardQuota);
    return (
      <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed border-slate-200 p-10 text-center">
        <ListBulletIcon className="h-8 w-8 text-slate-400" />
        <p className="max-w-md text-sm text-slate-600">
          {!closed
            ? "The award list can be made once the call closes."
            : !canDecide
              ? "HEC has not made the award list yet."
              : `Start the award list from the ranking: ${top ? `${top} awarded what they asked for; everyone else, including applications not fully marked yet, is not` : "nobody is awarded yet, as the call has no quota"}. You can adjust it before sending it to ${sendTo}.`}
        </p>
        {closed && canDecide && (
          <Button icon={<ListBulletIcon className="h-4 w-4" />} onClick={create} loading={busy}>
            Create award list
          </Button>
        )}
      </div>
    );
  }
  if (isError) return <EmptyState title="Could not load the award list" onRefresh={refetch} />;

  const finalised = list.state === "FINALISED";
  const editable = canDecide && !finalised;
  const changed = entries !== null;
  const counts = shown.reduce((acc, e) => ({ ...acc, [e.outcome]: (acc[e.outcome] ?? 0) + 1 }), {});
  const changeable = editable ? shown.filter((e) => !e.decidedEarlier) : [];
  const allSelected = changeable.length > 0 && changeable.every((e) => selected.includes(e.applicationId));
  const checkbox = (props) => <input type="checkbox" className="h-4 w-4 accent-brand" disabled={busy} {...props} />;

  const columns = [
    ...(changeable.length
      ? [
          {
            key: "select",
            label: checkbox({ "aria-label": "Select every applicant", checked: allSelected, onChange: () => setSelected(allSelected ? [] : changeable.map((e) => e.applicationId)) }),
            exportable: false,
            sortable: false,
            render: (e) =>
              e.decidedEarlier
                ? null
                : checkbox({ "aria-label": `Select ${e.applicantName}`, checked: selected.includes(e.applicationId), onClick: (ev) => ev.stopPropagation(), onChange: () => toggle(e.applicationId) }),
          },
        ]
      : []),
    { key: "rank", label: "Rank", accessor: (e) => e.rank ?? "—", sortValue: (e) => e.rank ?? 999 },
    { key: "applicant", label: "Applicant", accessor: (e) => e.applicantName, render: (e) => <ApplicantName name={e.applicantName} meets={e.meetsEligibility} /> },
    { key: "programme", label: "Programme", accessor: (e) => e.programmeName },
    {
      key: "score",
      label: "Average score",
      accessor: (e) => formatScore(e.averageScore),
      sortValue: (e) => e.averageScore,
      render: (e) => (
        <span className="flex flex-col gap-0.5">
          {formatScore(e.averageScore)}
          {!e.reviewed && !e.decidedEarlier && <span className="text-xs text-amber-700">Still being marked</span>}
        </span>
      ),
    },
    {
      key: "outcome",
      label: "Outcome",
      accessor: (e) => outcomeOf(e.outcome).label,
      render: (e) =>
        editable && !e.decidedEarlier ? (
          <Select aria-label={`Outcome for ${e.applicantName}`} value={e.outcome} options={OUTCOMES} onChange={(ev) => setOutcomes([e.applicationId], ev.target.value)} className="w-40" disabled={busy} />
        ) : (
          <span className="flex flex-col gap-0.5">
            <Tag color={outcomeOf(e.outcome).color}>{outcomeOf(e.outcome).label}</Tag>
            {e.decidedEarlier && <span className="text-xs text-slate-500">Decided earlier</span>}
          </span>
        ),
    },
    { key: "reserveRank", label: "Reserve rank", accessor: (e) => e.reserveRank ?? "—", sortValue: (e) => e.reserveRank ?? 999 },
    {
      key: "loan",
      label: "Approved loan",
      accessor: (e) => (e.outcome === "AWARDED" ? loanSummary(e.approvedAmounts) : "—"),
      render: (e) =>
        e.outcome === "AWARDED" ? (
          <span className="flex items-center gap-1">
            <span className={e.approvedAmounts?.tuitionFee == null ? "text-amber-700" : "text-slate-800"}>{loanSummary(e.approvedAmounts)}</span>
            {editable && !e.decidedEarlier && <RowAction Icon={PencilSquareIcon} label="Set loan amounts" onClick={() => setFundingFor(e)} disabled={busy} />}
          </span>
        ) : (
          "—"
        ),
    },
    ...(finalised
      ? [
          {
            key: "state",
            label: "Now",
            accessor: (e) => applicationStateOf(e.state).label,
            render: (e) => <Tag color={applicationStateOf(e.state).color}>{applicationStateOf(e.state).label}</Tag>,
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-4">
      {finalised && <InfoBox>Sent {formatDateTime(list.finalisedAt)}. Awards are with the DG and the Minister for approval.</InfoBox>}
      <div className="flex flex-wrap items-center gap-2">
        {OUTCOMES.map((o) => (
          <Tag key={o.value} color={o.color}>
            {o.label}: {counts[o.value] ?? 0}
          </Tag>
        ))}
        {list.awardQuota != null && <span className="text-sm text-slate-500">Places: {list.awardQuota}</span>}
        {changed && <span className="text-sm text-amber-700">Unsaved changes</span>}
      </div>
      {selected.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 p-3">
          <span className="text-sm font-medium text-slate-700">{selected.length} selected:</span>
          {OUTCOMES.map((o) => (
            <Button key={o.value} size="sm" variant="outline" onClick={() => setOutcomes(selected, o.value)} disabled={busy}>
              {o.label}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => setSelected([])} disabled={busy}>
            Clear selection
          </Button>
        </div>
      )}
      <Table columns={columns} data={shown} searchable={false} exportName={`award-list-${call.name}`} emptyTitle="No submitted applications" />
      {problems.length > 0 && (
        <ul className="list-disc pl-5 text-sm text-red-600">
          {problems.map((message, i) => (
            <li key={i}>{message}</li>
          ))}
        </ul>
      )}
      {editable && (
        <div className="flex flex-col-reverse justify-end gap-3 sm:flex-row">
          <Button variant="ghost" onClick={() => setEntries(null)} disabled={busy || !changed}>
            Discard changes
          </Button>
          <Button variant="outline" icon={<CheckIcon className="h-4 w-4" />} onClick={save} loading={busy && changed} disabled={!changed}>
            Save award list
          </Button>
          <Button icon={<CheckBadgeIcon className="h-4 w-4" />} onClick={() => setConfirming(true)} disabled={busy || changed}>
            Send to {sendTo}
          </Button>
        </div>
      )}
      <ApprovedFundingDialog
        key={fundingFor?.applicationId}
        entry={fundingEntryOf(fundingFor)}
        onApply={(amounts) => setAmounts(fundingFor.applicationId, amounts)}
        onClose={() => setFundingFor(null)}
      />
      <ConfirmModal
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={finalise}
        loading={busy}
        title={`Send the award list to ${sendTo}?`}
        description={`${counts.AWARDED ?? 0} awarded go to ${sendTo} for approval with their approved loans; ${counts.RESERVE ?? 0} on the reserve list and ${counts.NOT_AWARDED ?? 0} not awarded are told now. This cannot be undone.`}
        confirmLabel={`Send to ${sendTo}`}
      />
    </div>
  );
}
